import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useEffect, useMemo } from "react";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowRight } from "lucide-react";
import { navigate } from "wouter/use-browser-location";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import { posApiFetch } from "@/utils/pos-api";
import type { Location, OrderItem } from "@/types/pos";

const fetchProducts = async () => {
  const res = await posApiFetch("/v1/pos/products");
  if (!res.ok) throw new Error("Failed to fetch products");
  return res.json();
};

const fetchOrders = async () => {
  const res = await posApiFetch("/v1/pos/orders");
  if (!res.ok) throw new Error("Failed to fetch orders");
  return res.json();
};

const fetchLocations = async () => {
  const res = await posApiFetch("/v1/pos/locations");
  if (!res.ok) throw new Error("Failed to fetch locations");
  return res.json();
};

const fetchMenuTypes = async () => {
  const res = await posApiFetch("/v1/pos/menu-types");
  if (!res.ok) throw new Error("Failed to fetch menu types");
  return res.json();
};

type OrdersState = {
  location?: string;
  status?: string;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  dateRange?: { from: string; to: string };
  priceRange?: { min: string; max: string };
};

const defaultOrdersState: OrdersState = {
  location: "all",
  status: "all",
  currentPage: 1,
  itemsPerPage: 10,
  priceRange: { min: "", max: "" },
};

export default function Dashboard() {
  useEffect(() => {
    document.title = "Dashboard | SECH Admin";
  }, []);

  const [ordersState] = usePagePersistentState<OrdersState>("orders", defaultOrdersState);
  const selectedLocation = ordersState.location;
  const { formatDate } = useDateFormatter();
  const { sortColumn, sortDirection, handleSort } = useSortableTable<
    "createdAt" | "orderNumber" | "status" | "totalUsd"
  >({
    storageKey: "dashboard_recent_orders",
    defaultColumn: "createdAt",
    defaultDirection: "desc",
  });

  const { data: products = [], error: productsError } = useQuery({
    queryKey: ["/v1/pos/products"],
    queryFn: fetchProducts,
    retry: 3,
    retryDelay: 1000,
  });

  const { data: orders = [], isLoading: ordersLoading, error: ordersError } = useQuery({
    queryKey: ["/v1/pos/orders"],
    queryFn: fetchOrders,
    retry: 3,
    retryDelay: 1000,
  });

  const { data: locations = [], error: locationsError } = useQuery({
    queryKey: ["/v1/pos/locations"],
    queryFn: fetchLocations,
    retry: 3,
    retryDelay: 1000,
  });

  const { data: menuTypes = [], error: menuTypesError } = useQuery({
    queryKey: ["/v1/pos/menu-types"],
    queryFn: fetchMenuTypes,
    retry: 3,
    retryDelay: 1000,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "preparing": return "bg-blue-100 text-blue-800 border-blue-200";
      case "ready": return "bg-green-100 text-green-800 border-green-200";
      case "delivered": return "bg-gray-100 text-gray-800 border-gray-200";
      case "cancelled": return "bg-red-100 text-red-800 border-red-200";
      case "completed": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-800";
      case "preparing": return "bg-blue-800";
      case "ready": return "bg-green-800";
      case "delivered": return "bg-gray-800";
      case "cancelled": return "bg-red-800";
      case "completed": return "bg-green-800";
      default: return "bg-gray-800";
    }
  };

  const setTableColumns = useMemo(
    () => [
      { label: "Order Number", field: "orderNumber", sortable: true },
      {
        label: "Items",
        field: "items",
        cellTemplate: (items: OrderItem[]) => {
          if (!items || items.length === 0) return <span className="text-[13px] text-[var(--secondary-text-color)]">—</span>;
          const [first, ...rest] = items;
          const getItemLabel = (item: any) => {
            const name = item?.product?.name || item?.customDrink?.name || item?.name || "Item";
            const qty = item.quantity ?? 1;
            return `${name} × ${qty}`;
          };
          return (
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-[#505258]">{getItemLabel(first)}</span>
              {rest.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="h-[26px] w-[26px] rounded-full bg-[var(--secondary-background-color)] text-[12px] leading-[14px] text-[#53585B] font-medium flex items-center justify-center hover:bg-[var(--primary-background-hover-color)]">
                      {rest.length}+
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[180px] p-0 rounded-[6px]">
                    {rest.map((item: any) => (
                      <DropdownMenuItem key={item.id} className="flex items-center gap-2 border-b border-[#E4E5EB] text-[13px]">
                        <span className="text-[12px] leading-[14px] font-medium text-[#53585B]">{getItemLabel(item)}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        },
      },
      {
        label: "Date & Time", field: "createdAt", sortable: true,
        cellTemplate: (value: string) => formatDate(value),
      },
      {
        label: "Pickup Person", field: "user",
        cellTemplate: (value: any, row: any) => (
          <div className="flex items-center gap-2">
            {value && <NameAvatar name={value?.pickupPerson || "-"} imageUrl={row?.imageUrl} />}
            <span className="text-[13px] text-[#505258]">{value?.pickupPerson || "-"}</span>
          </div>
        ),
      },
      {
        label: "Status", field: "status", sortable: true,
        cellTemplate: (value: string) => (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColor(value)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(value)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{value.charAt(0).toUpperCase() + value.slice(1)}</span>
          </span>
        ),
      },
      {
        label: "Location", field: "location",
        cellTemplate: (value: Location) => (
          <span className="text-[13px] flex items-center gap-2">
            <span className="h-[18px] w-[18px] rounded-[4px]" style={{ backgroundColor: value?.colorCode }} />
            <span className="text-[var(--secondary-text-color)] text-[13px] leading-5 font-normal mt-1 rounded-xl">{value?.name || "—"}</span>
          </span>
        ),
        hidden: selectedLocation !== "all",
      },
      {
        label: "Location Details", field: "locationDetails",
        cellTemplate: (value: string) => <span className="text-[13px] text-[#505258] text-center">{value || "-"}</span>,
      },
      {
        label: "Total (USD)", field: "totalUsd", sortable: true,
        cellTemplate: (value: string) => <span className="text-[13px] text-[var(--secondary-text-color)]">${value}</span>,
      },
    ],
    [selectedLocation]
  );

  const pagination = {
    totalPages: Math.ceil(orders.length / 10),
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: orders.length,
    handlePageChange: () => {},
    handleItemsPerPageChange: () => {},
  };

  const newOrders = useMemo(() => {
    if (!orders || !Array.isArray(orders)) return [];
    const filtered = orders.filter((o: any) => (o?.status ?? "").toLowerCase() === "pending");
    return filtered.sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "orderNumber": aVal = a?.orderNumber ?? ""; bVal = b?.orderNumber ?? ""; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        case "status": aVal = a?.status ?? ""; bVal = b?.status ?? ""; break;
        case "totalUsd": aVal = parseFloat(a?.totalUsd ?? "0"); bVal = parseFloat(b?.totalUsd ?? "0"); break;
        default: aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      let comparison = 0;
      if (typeof aVal === "string" && typeof bVal === "string") comparison = aVal.localeCompare(bVal);
      else comparison = aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [orders, sortColumn, sortDirection]);

  if (productsError || ordersError || locationsError || menuTypesError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">
              {productsError?.message || ordersError?.message || locationsError?.message || menuTypesError?.message || "Failed to load data"}
            </p>
            <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleViewAllOrders = () => {
    const raw = localStorage.getItem("sech_pos_admin");
    if (raw) {
      const parsed = JSON.parse(raw);
      delete parsed.orders;
      localStorage.setItem("sech_pos_admin", JSON.stringify(parsed));
    }
    navigate("/admin/orders");
  };

  return (
    <div className="h-full flex flex-col">
      <div className="dashboard-header h-[48px] px-[16px] flex items-center w-full border-b border-[var(--layout-border-color)] shrink-0">
        <span className="text-[20px] leading-[30px] font-bold">Dashboard</span>
      </div>
      <div className="px-[16px] pt-[24px] flex-1 flex flex-col overflow-hidden">
        <Card className="border-0 flex-1 flex flex-col overflow-hidden p-[16px]">
          <CardHeader className="p-0 pb-[12px] space-y-0 flex flex-row items-center justify-between mb-0 shrink-0">
            <CardTitle className="text-[20px] leading-[30px] font-bold">Recent Orders</CardTitle>
            {newOrders.length > 0 && (
              <Button onClick={handleViewAllOrders} className="h-[32px] gap-1 flex px-2 rounded-[4px] group transition-all duration-300" variant="ghost">
                <span className="text-[13px] leading-[15px] font-medium text-[#172b4d]">View All</span>
                <ArrowRight className="h-4 w-3 ml-1 transform transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            {!orders.length && !ordersLoading ? (
              <p className="text-gray-500 text-center py-4">No orders yet</p>
            ) : (
              <div className="pt-3 h-full">
                {newOrders.length || ordersLoading ? (
                  <CommonGrid
                    rowData={newOrders.slice(0, 12)}
                    columns={setTableColumns}
                    isPaginationRequired={false}
                    pagination={pagination}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort as (column: string) => void}
                  />
                ) : (
                  <NoRecordFound
                    title="pending orders"
                    description="No pending orders are available at this time."
                    action={
                      <Button
                        variant="outline"
                        className="text-[var(--primary-color)] h-[38px] bg-transparent border border-[var(--primary-color)] hover:bg-[var(--primary-color)] hover:text-[var(--primary-background-color)]"
                        onClick={handleViewAllOrders}
                      >
                        View All Orders
                      </Button>
                    }
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
